import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useStore } from '../../store/store';
import { IPreviewShareLink } from '../../store/previewStore';
import { getCookie, setCookie } from '../../utils/cookies';
import { createDefaultPreviewDateInput, fromPreviewDateInput, toPreviewDateInput } from '../../utils/previewDates';
import { flattenPreviewPages } from '../../utils/previewPages';

const PREVIEW_ACCESS_PUBLIC = 1;
const PREVIEW_ACCESS_INVITED = 3;
const ADVANCED_STATE_COOKIE = 'creator_preview_link_advanced_open';
const ADVANCED_STATE_COOKIE_DAYS = 30;
const LOWERCASE_CHARS = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const NUMBER_CHARS = '0123456789';
const SYMBOL_CHARS = '!@#$%^&*';
const SHARE_PUBLIC_ID_CHARS = `${LOWERCASE_CHARS}${UPPERCASE_CHARS}${NUMBER_CHARS}`;

type TShareLinkDialogMode = 'create' | 'edit';

interface IUseShareLinkFormOptions {
  open: boolean;
  courseId: number;
  coursePageId?: number;
  mode?: TShareLinkDialogMode;
  link?: IPreviewShareLink | null;
  onSaved?: (link: IPreviewShareLink) => void;
}

interface IShareLinkFormState {
  previewName: string;
  expirationEnabled: boolean;
  expirationValue: string;
  password: string;
  passwordEnabled: boolean;
  maxViews: string;
  maxViewsEnabled: boolean;
  startPageEnabled: boolean;
  startPageId: number | null;
  allowComments: boolean;
  privateComments: boolean;
  publicId: string;
  shareUrl: string;
  isSaved: boolean;
  savedRequiresPassword: boolean;
}

function getSecureRandomNumber(maxExclusive: number): number {
  if (maxExclusive <= 0) {
    return 0;
  }

  if (window.crypto?.getRandomValues) {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0] % maxExclusive;
  }

  return Math.floor(Math.random() * maxExclusive);
}

function pickRandomChar(charset: string): string {
  return charset[getSecureRandomNumber(charset.length)];
}

function shuffleString(value: string): string {
  const items = value.split('');
  for (let i = items.length - 1; i > 0; i--) {
    const j = getSecureRandomNumber(i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items.join('');
}

function generatePassword(length = 14): string {
  const requiredChars = [pickRandomChar(LOWERCASE_CHARS), pickRandomChar(UPPERCASE_CHARS), pickRandomChar(NUMBER_CHARS), pickRandomChar(SYMBOL_CHARS)];
  const allChars = `${LOWERCASE_CHARS}${UPPERCASE_CHARS}${NUMBER_CHARS}${SYMBOL_CHARS}`;

  while (requiredChars.length < length) {
    requiredChars.push(pickRandomChar(allChars));
  }

  return shuffleString(requiredChars.join(''));
}

function generateSharePublicId(length = 32): string {
  let value = '';
  while (value.length < length) {
    value += pickRandomChar(SHARE_PUBLIC_ID_CHARS);
  }
  return value;
}

function isFutureDateValue(value: string): boolean {
  const parsed = fromPreviewDateInput(value);
  if (!parsed) {
    return false;
  }

  const date = new Date(parsed);
  return !Number.isNaN(date.getTime()) && date > new Date();
}

async function copyText(text: string): Promise<boolean> {
  if (!text.trim()) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function buildCreateModeFormState(coursePageId: number | undefined, isPasswordAvailable: boolean, buildShareUrl: (publicId: string) => string): IShareLinkFormState {
  const nextPublicId = generateSharePublicId();

  return {
    previewName: '',
    expirationEnabled: true,
    expirationValue: createDefaultPreviewDateInput(),
    password: isPasswordAvailable ? generatePassword() : '',
    passwordEnabled: isPasswordAvailable,
    maxViews: '',
    maxViewsEnabled: false,
    startPageEnabled: false,
    startPageId: typeof coursePageId === 'number' && coursePageId > 0 ? coursePageId : null,
    allowComments: true,
    privateComments: false,
    publicId: nextPublicId,
    shareUrl: buildShareUrl(nextPublicId),
    isSaved: false,
    savedRequiresPassword: false,
  };
}

function buildEditModeFormState(link: IPreviewShareLink): IShareLinkFormState {
  return {
    previewName: link.previewName?.trim() ?? '',
    expirationEnabled: link.expiresAtUtc !== null && link.expiresAtUtc !== undefined,
    expirationValue: toPreviewDateInput(link.expiresAtUtc),
    password: '',
    passwordEnabled: link.requiresPassword,
    maxViews: typeof link.maxViews === 'number' ? `${link.maxViews}` : '',
    maxViewsEnabled: typeof link.maxViews === 'number',
    startPageEnabled: typeof link.coursePageId === 'number',
    startPageId: typeof link.coursePageId === 'number' ? link.coursePageId : null,
    allowComments: link.allowComments ?? true,
    privateComments: link.privateComments ?? link.commentVisibilityMode === 2,
    publicId: link.publicId,
    shareUrl: link.shareUrl,
    isSaved: true,
    savedRequiresPassword: link.requiresPassword,
  };
}

export default function useShareLinkForm({ open, courseId, coursePageId, mode = 'create', link = null, onSaved }: IUseShareLinkFormOptions) {
  const { previewStore, courseStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = mode === 'edit';
  const [savedLink, setSavedLink] = useState<IPreviewShareLink | null>(isEditMode ? link : null);
  const activeLink = savedLink ?? link;
  const effectiveIsEditMode = isEditMode || savedLink !== null;
  const isPasswordAvailable = (activeLink?.accessMode ?? PREVIEW_ACCESS_PUBLIC) !== PREVIEW_ACCESS_INVITED;
  const initialState = useMemo<IShareLinkFormState>(
    () => (isEditMode && link ? buildEditModeFormState(link) : buildCreateModeFormState(coursePageId, isPasswordAvailable, previewStore.buildShareUrl)),
    [coursePageId, isEditMode, isPasswordAvailable, link, previewStore],
  );
  const [previewName, setPreviewName] = useState(initialState.previewName);
  const [expirationEnabled, setExpirationEnabled] = useState(initialState.expirationEnabled);
  const [expirationValue, setExpirationValue] = useState(initialState.expirationValue);
  const [password, setPassword] = useState(initialState.password);
  const [passwordEnabled, setPasswordEnabled] = useState(initialState.passwordEnabled);
  const [maxViews, setMaxViews] = useState(initialState.maxViews);
  const [maxViewsEnabled, setMaxViewsEnabled] = useState(initialState.maxViewsEnabled);
  const [startPageEnabled, setStartPageEnabled] = useState(initialState.startPageEnabled);
  const [startPageId, setStartPageId] = useState<number | null>(initialState.startPageId);
  const [allowComments, setAllowComments] = useState(initialState.allowComments);
  const [privateComments, setPrivateComments] = useState(initialState.privateComments);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publicId, setPublicId] = useState(initialState.publicId);
  const [shareUrl, setShareUrl] = useState(initialState.shareUrl);
  const [isSaved, setIsSaved] = useState(initialState.isSaved);
  const [advancedOpen, setAdvancedOpen] = useState(() => getCookie(ADVANCED_STATE_COOKIE) === 'true');
  const [savedRequiresPassword, setSavedRequiresPassword] = useState(initialState.savedRequiresPassword);

  const pageOptions = useMemo(() => flattenPreviewPages(courseStore.courseViewModel?.pages), [courseStore.courseViewModel?.pages]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextState = isEditMode && link ? buildEditModeFormState(link) : buildCreateModeFormState(coursePageId, isPasswordAvailable, previewStore.buildShareUrl);

    setSavedLink(isEditMode ? link : null);
    setAdvancedOpen(getCookie(ADVANCED_STATE_COOKIE) === 'true');
    setIsSubmitting(false);
    setPreviewName(nextState.previewName);
    setExpirationEnabled(nextState.expirationEnabled);
    setExpirationValue(nextState.expirationValue);
    setPassword(nextState.password);
    setPasswordEnabled(nextState.passwordEnabled);
    setMaxViews(nextState.maxViews);
    setMaxViewsEnabled(nextState.maxViewsEnabled);
    setStartPageEnabled(nextState.startPageEnabled);
    setStartPageId(nextState.startPageId);
    setAllowComments(nextState.allowComments);
    setPrivateComments(nextState.privateComments);
    setPublicId(nextState.publicId);
    setShareUrl(nextState.shareUrl);
    setIsSaved(nextState.isSaved);
    setSavedRequiresPassword(nextState.savedRequiresPassword);
  }, [coursePageId, isEditMode, isPasswordAvailable, link, open, previewStore]);

  const handleAdvancedToggle = useMemo(
    () => () => {
      setAdvancedOpen((current) => {
        const nextValue = !current;
        setCookie(ADVANCED_STATE_COOKIE, String(nextValue), ADVANCED_STATE_COOKIE_DAYS);
        return nextValue;
      });
    },
    [],
  );

  const handleStartPageEnabledChange = useMemo(
    () => (value: boolean) => {
      setStartPageEnabled(value);
      if (!value) {
        return;
      }

      if (startPageId === null && coursePageId && coursePageId > 0) {
        setStartPageId(coursePageId);
        return;
      }

      if (startPageId === null && pageOptions.length > 0) {
        setStartPageId(pageOptions[0].id);
      }
    },
    [coursePageId, pageOptions, startPageId],
  );

  const handlePasswordEnabledChange = useMemo(
    () => (value: boolean) => {
      setPasswordEnabled(value);
      if (value && password.trim() === '') {
        setPassword(generatePassword());
      }
    },
    [password],
  );

  const handleMaxViewsEnabledChange = useMemo(
    () => (value: boolean) => {
      setMaxViewsEnabled(value);
      if (value && maxViews.trim() === '') {
        setMaxViews('1');
      }
    },
    [maxViews],
  );

  const handleExpirationEnabledChange = useMemo(
    () => (value: boolean) => {
      setExpirationEnabled(value);
      if (value && expirationValue.trim() === '') {
        setExpirationValue(createDefaultPreviewDateInput());
      }
    },
    [expirationValue],
  );

  const copyShareUrl = useMemo(
    () => async () => {
      const copied = await copyText(shareUrl);
      enqueueSnackbar(copied ? 'Copied share link to clipboard.' : 'Unable to copy share link.', { variant: copied ? 'success' : 'warning' });
    },
    [enqueueSnackbar, shareUrl],
  );

  const copyPassword = useMemo(
    () => async () => {
      const copied = await copyText(password);
      enqueueSnackbar(copied ? 'Copied password to clipboard.' : 'Unable to copy password.', { variant: copied ? 'success' : 'warning' });
    },
    [enqueueSnackbar, password],
  );

  const saveLink = useMemo(
    () => async () => {
      const resolvedPageId = startPageEnabled ? (startPageId ?? undefined) : undefined;
      const isSharedCourse = courseStore.courseProperties?.courseID === courseId ? Boolean(courseStore.courseProperties.sharedToOwner) : false;
      const trimmedPreviewName = previewName.trim() !== '' ? previewName.trim() : undefined;

      if (startPageEnabled && resolvedPageId === undefined) {
        enqueueSnackbar('Select a start page.', { variant: 'error' });
        return;
      }

      if (!isSaved && isSharedCourse) {
        setIsSubmitting(true);
        try {
          const sharedPreviewUrl = await previewStore.getLegacyPreview(courseId, resolvedPageId);
          if (!sharedPreviewUrl) {
            enqueueSnackbar('Unable to generate preview URL.', { variant: 'error' });
            return;
          }

          setShareUrl(sharedPreviewUrl);
          const copied = await copyText(sharedPreviewUrl);
          enqueueSnackbar(copied ? 'Copied temporary preview URL for shared course.' : 'Temporary preview URL created, but copy failed.', {
            variant: copied ? 'success' : 'warning',
          });
        } catch {
          enqueueSnackbar('Unable to generate preview URL.', { variant: 'error' });
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      const expiresAtUtc = expirationEnabled ? fromPreviewDateInput(expirationValue) : null;
      if (expirationEnabled && (!expiresAtUtc || !isFutureDateValue(expirationValue))) {
        enqueueSnackbar('Expiry must be a valid future date.', { variant: 'error' });
        return;
      }

      let parsedMaxViews: number | undefined;
      if (maxViewsEnabled && maxViews.trim() !== '') {
        parsedMaxViews = Number.parseInt(maxViews, 10);
        if (!Number.isInteger(parsedMaxViews) || parsedMaxViews <= 0) {
          enqueueSnackbar('Max views must be a positive whole number.', { variant: 'error' });
          return;
        }
      }

      const trimmedPassword = isPasswordAvailable && passwordEnabled && password.trim() !== '' ? password.trim() : undefined;
      const canKeepExistingPassword = isSaved && savedRequiresPassword && passwordEnabled && password.trim() === '';
      if (isPasswordAvailable && passwordEnabled && !trimmedPassword && !canKeepExistingPassword) {
        enqueueSnackbar('Password is enabled. Enter a password.', { variant: 'error' });
        return;
      }

      const removePassword = !isPasswordAvailable || !passwordEnabled;
      const persistedPrivateComments = allowComments ? privateComments : false;

      setIsSubmitting(true);
      try {
        const response = !effectiveIsEditMode
          ? await previewStore.createShareLink(
              courseId,
              resolvedPageId,
              expiresAtUtc,
              trimmedPassword,
              parsedMaxViews,
              'en',
              trimmedPreviewName,
              PREVIEW_ACCESS_PUBLIC,
              allowComments,
              persistedPrivateComments,
              [],
              publicId,
            )
          : await previewStore.updateShareLink(
              activeLink?.publicId ?? publicId,
              resolvedPageId,
              'en',
              expiresAtUtc,
              canKeepExistingPassword ? undefined : trimmedPassword,
              removePassword,
              parsedMaxViews,
              trimmedPreviewName,
              activeLink?.accessMode ?? PREVIEW_ACCESS_PUBLIC,
              allowComments,
              persistedPrivateComments,
              activeLink?.invitedEmails ?? [],
            );

        setSavedLink(response);
        setPublicId(response.publicId);
        setShareUrl(response.shareUrl);
        setPreviewName(response.previewName?.trim() ?? '');
        setIsSaved(true);
        setSavedRequiresPassword(response.requiresPassword);
        setPasswordEnabled(response.requiresPassword);
        setPassword((currentPassword) => (response.requiresPassword ? currentPassword : ''));
        setMaxViews(typeof response.maxViews === 'number' ? `${response.maxViews}` : '');
        setMaxViewsEnabled(typeof response.maxViews === 'number');

        const copied = await copyText(response.shareUrl);
        enqueueSnackbar(copied ? 'Share link saved and copied to clipboard.' : 'Share link saved, but failed to copy to clipboard.', {
          variant: copied ? 'success' : 'warning',
        });
        onSaved?.(response);
      } catch (error: any) {
        const status = error?.response?.status;
        const canFallbackToSharedPreview = !effectiveIsEditMode && !isSaved && (isSharedCourse || status === 401 || status === 403 || status === 404);

        if (canFallbackToSharedPreview) {
          try {
            const sharedPreviewUrl = await previewStore.getLegacyPreview(courseId, resolvedPageId);
            if (sharedPreviewUrl) {
              setShareUrl(sharedPreviewUrl);
              const copied = await copyText(sharedPreviewUrl);
              enqueueSnackbar(copied ? 'Copied temporary preview URL for shared course.' : 'Temporary preview URL created, but copy failed.', {
                variant: copied ? 'success' : 'warning',
              });
              return;
            }
          } catch {
            // Fall through to the generic error.
          }
        }

        enqueueSnackbar('Unable to save share link.', { variant: 'error' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      allowComments,
      courseId,
      courseStore.courseProperties?.courseID,
      courseStore.courseProperties?.sharedToOwner,
      enqueueSnackbar,
      expirationEnabled,
      expirationValue,
      effectiveIsEditMode,
      isEditMode,
      isPasswordAvailable,
      isSaved,
      maxViews,
      maxViewsEnabled,
      onSaved,
      password,
      passwordEnabled,
      activeLink,
      previewStore,
      previewName,
      privateComments,
      publicId,
      savedRequiresPassword,
      savedLink,
      startPageEnabled,
      startPageId,
    ],
  );

  return {
    advancedOpen,
    allowComments,
    copyPassword,
    copyShareUrl,
    expirationEnabled,
    expirationValue,
    handleAdvancedToggle,
    handleExpirationEnabledChange,
    handleMaxViewsEnabledChange,
    handlePasswordEnabledChange,
    handleStartPageEnabledChange,
    isEditMode,
    isPasswordAvailable,
    isSaved,
    isSubmitting,
    maxViews,
    maxViewsEnabled,
    pageOptions,
    password,
    passwordEnabled,
    privateComments,
    previewName,
    publicId,
    saveLink,
    savedRequiresPassword,
    setAllowComments,
    setExpirationValue,
    setMaxViews,
    setPassword,
    setPrivateComments,
    setPreviewName,
    setStartPageId,
    shareUrl,
    startPageEnabled,
    startPageId,
  };
}
